import { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { doc, setDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { getOrCreateAccount, updateAccountDoc, getUsageDoc } from '../../utils/accountStorage';
import { TIER_ORDER, TIER_NAMES, TIER_PRICES } from '../../config/tierDefaults';

function AdminEditUser({ user, onBack }) {
  const [account,              setAccount]              = useState(null);
  const [usage,                setUsage]                = useState(null);
  const [tier,                 setTier]                 = useState('basic');
  const [scanCreditsExtra,     setScanCreditsExtra]     = useState(0);
  const [scanCreditsDuration,  setScanCreditsDuration]  = useState(30);
  const [isAdmin,              setIsAdmin]              = useState(false);
  const [saving,               setSaving]               = useState(false);
  const [saved,                setSaved]                = useState(false);
  const [error,                setError]                = useState(null);

  useEffect(() => {
    Promise.all([getOrCreateAccount(user.uid), getUsageDoc(user.uid)]).then(([acc, usg]) => {
      setAccount(acc);
      setUsage(usg);
      setTier(acc?.tier || 'basic');
      setScanCreditsExtra(acc?.scanCreditsExtra || 0);
      setScanCreditsDuration(acc?.scanCreditsDuration || 30);
      setIsAdmin(acc?.isAdmin || false);
    });
  }, [user.uid]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const credits  = Number(scanCreditsExtra);
      const duration = Math.max(1, Number(scanCreditsDuration) || 30);
      const expiresAt = credits > 0
        ? Timestamp.fromMillis(Date.now() + duration * 24 * 60 * 60 * 1000)
        : null;

      await updateAccountDoc(user.uid, {
        tier,
        scanCreditsExtra:    credits,
        scanCreditsDuration: duration,
        scanCreditsExpiresAt: expiresAt,
        isAdmin,
      });
      // Sync admins collection (used by Firestore security rules)
      if (isAdmin) {
        await setDoc(doc(db, 'admins', user.uid), { uid: user.uid, email: user.email });
      } else {
        await deleteDoc(doc(db, 'admins', user.uid)).catch(() => {}); // ok if doc doesn't exist
      }
      // Reload account so expiry date reflects the newly saved value
      const updated = await getOrCreateAccount(user.uid);
      setAccount(updated);

      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('AdminEditUser save failed:', err);
      setError('Save failed — ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const monthKey   = new Date().toISOString().slice(0, 7);
  const scansUsed  = (usage && usage.monthKey === monthKey) ? (usage.scansThisMonth || 0) : 0;

  const Row = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #F3F4F6', fontSize: 13 }}>
      <span style={{ color: '#6B7280' }}>{label}</span>
      <span style={{ color: '#111', fontWeight: 600 }}>{value}</span>
    </div>
  );

  return (
    <div>
      <button
        onClick={onBack}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#0A7C8A', fontWeight: 700, fontSize: 13, marginBottom: 16, padding: 0 }}
      >
        <ArrowLeft size={14} /> Back to Users
      </button>

      <div style={{ fontWeight: 800, fontSize: 16, color: '#111', marginBottom: 2 }}>{user.email}</div>
      {user.displayName && <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 16 }}>{user.displayName}</div>}

      {account ? (
        <>
          <Row label="UID" value={<span style={{ fontFamily: 'monospace', fontSize: 11 }}>{user.uid}</span>} />
          <Row label="Scans this month" value={scansUsed} />
          <Row label="Current tier" value={TIER_NAMES[account.tier] || account.tier} />

          {/* Tier */}
          <div style={{ marginTop: 20 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 6 }}>
              Change Tier
            </label>
            <select
              value={tier}
              onChange={e => setTier(e.target.value)}
              style={{ width: '100%', padding: '9px 10px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#374151', outline: 'none', background: '#fff', fontFamily: 'inherit' }}
            >
              {TIER_ORDER.map(t => (
                <option key={t} value={t}>{TIER_NAMES[t]} — {TIER_PRICES[t]}</option>
              ))}
            </select>
          </div>

          {/* Bonus scan credits */}
          <div style={{ marginTop: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 6 }}>
              Bonus Scan Credits
            </label>
            <input
              type="number"
              min={0}
              value={scanCreditsExtra}
              onChange={e => setScanCreditsExtra(Math.max(0, parseInt(e.target.value) || 0))}
              style={{ width: '100%', padding: '9px 10px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#374151', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <label style={{ fontSize: 11, color: '#6B7280', whiteSpace: 'nowrap' }}>Expires after</label>
              <input
                type="number"
                min={1}
                value={scanCreditsDuration}
                onChange={e => setScanCreditsDuration(Math.max(1, parseInt(e.target.value) || 30))}
                style={{ width: 64, padding: '6px 8px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#374151', outline: 'none', fontFamily: 'inherit', textAlign: 'center' }}
              />
              <label style={{ fontSize: 11, color: '#6B7280' }}>days</label>
            </div>
            {(() => {
              const raw = account?.scanCreditsExpiresAt;
              if (!raw || !account?.scanCreditsExtra) return (
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                  Consumed before monthly scans. Saving will reset the expiry timer.
                </div>
              );
              const ms = raw.toMillis ? raw.toMillis() : Number(raw);
              const expired = ms < Date.now();
              const dateStr = new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
              return (
                <div style={{ fontSize: 11, color: expired ? '#DC2626' : '#6B7280', marginTop: 4 }}>
                  {expired ? `Expired on ${dateStr}` : `Current expiry: ${dateStr}`}
                </div>
              );
            })()}
          </div>

          {/* Admin flag */}
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="checkbox"
              id="chk-isAdmin"
              checked={isAdmin}
              onChange={e => setIsAdmin(e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <label htmlFor="chk-isAdmin" style={{ fontSize: 13, fontWeight: 700, color: '#374151', cursor: 'pointer' }}>
              Admin access
            </label>
          </div>

          {error && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#FEF2F2', borderRadius: 8, fontSize: 12, color: '#DC2626' }}>
              {error}
            </div>
          )}

          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              marginTop: 24, width: '100%', padding: '13px', borderRadius: 10,
              border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
              background: saved ? '#10B981' : 'linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)',
              color: '#fff', fontWeight: 700, fontSize: 14, transition: 'background 0.2s',
            }}
          >
            {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save Changes'}
          </button>
        </>
      ) : (
        <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '24px 0', fontSize: 13 }}>Loading…</div>
      )}
    </div>
  );
}

export default AdminEditUser;
